import * as React from "react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle } from "lucide-react";
import { Question } from "./index";
import { Link } from "react-router-dom";

interface ExamProps {
  questions: Question[];
  onComplete?: (results: ExamResult[]) => void;
  redirectURL?: string;
}

export interface ExamResult {
  questionIndex: number;
  selectedAnswer: number | null;
  isCorrect: boolean;
  correctAnswer: number;
  question: string;
  selectedAnswerText: string | null;
  correctAnswerText: string;
  summary: string;
}

export function Exam({ questions, onComplete,  redirectURL }: ExamProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [results, setResults] = useState<ExamResult[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex) / questions.length) * 100;

  const handleAnswerSelect = (value: string) => {
    setSelectedAnswer(parseInt(value));
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const result: ExamResult = {
      questionIndex: currentQuestionIndex,
      selectedAnswer,
      isCorrect,
      correctAnswer: currentQuestion.correctAnswer,
      question: currentQuestion.question,
      selectedAnswerText: selectedAnswer !== null ? currentQuestion.answers[selectedAnswer] : null,
      correctAnswerText: currentQuestion.answers[currentQuestion.correctAnswer],
      summary: `Q:${currentQuestion.question}\nR:${selectedAnswer !== null ? currentQuestion.answers[selectedAnswer] : 'Réponse non fournie'} ${isCorrect ? '<Correct>' : '<Incorrect>'}\n`,
    };

    setResults([...results, result]);
    setShowAnswer(true);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex === questions.length - 1) {
      // Exam is complete
      setIsComplete(true);
      onComplete?.(results);
      return;
    }

    // Move to next question
    setCurrentQuestionIndex(currentQuestionIndex + 1);
    setSelectedAnswer(null);
    setShowAnswer(false);
  };

  const getAnswerStatus = (answerIndex: number) => {
    if (!showAnswer) return null;
    
    if (answerIndex === currentQuestion.correctAnswer) {
      return "correct";
    }
    if (answerIndex === selectedAnswer && selectedAnswer !== currentQuestion.correctAnswer) {
      return "incorrect";
    }
    return null;
  };

  if (isComplete) {
    const correctAnswers = results.filter(r => r.isCorrect).length;
    const score = Math.round((correctAnswers / questions.length) * 100);

    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="text-center">Examen terminé!</CardTitle>
        </CardHeader>
        <CardContent className="text-center flex flex-col items-center justify-center">
          <div className="text-4xl font-bold text-primary">{score}%</div>
          <p className="text-muted-foreground">
            Vous avez répondu correctement à {correctAnswers} questions sur {questions.length}
          </p>
          {redirectURL && <Link to={redirectURL} className="w-full mt-8 px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-md">
              Prochaine étape
          </Link>}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Progress Section */}
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">
              Question {currentQuestionIndex} sur {questions.length}
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="w-full" />

      {/* Question Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">
            {currentQuestion.question}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={selectedAnswer?.toString() || ""}
            onValueChange={handleAnswerSelect}
            disabled={showAnswer}
          >
            {currentQuestion.answers.map((answer, index) => {
              const status = getAnswerStatus(index);
              return (
                <div
                  key={index}
                  className={`flex items-center space-x-2 p-3 rounded-lg border transition-colors ${
                    status === "correct"
                      ? "border-green-500 bg-green-50"
                      : status === "incorrect"
                      ? "border-red-500 bg-red-50"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  <RadioGroupItem
                    value={index.toString()}
                    id={`answer-${index}`}
                    className={status ? "opacity-50" : ""}
                  />
                  <label
                    htmlFor={`answer-${index}`}
                    className="flex-1 cursor-pointer"
                  >
                    {answer}
                  </label>
                  {status && (
                    <div className="ml-2">
                      {status === "correct" ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </RadioGroup>

          {/* Answer Explanation */}
          {showAnswer && currentQuestion.explanation!=="" && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">
                Explication:
              </h4>
              <p className="text-blue-800">{currentQuestion.explanation}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            {!showAnswer ? (
              <Button
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === null}
              >
                Valider la réponse
              </Button>
            ) : (
              <Button onClick={handleNextQuestion}>
                {currentQuestionIndex === questions.length - 1
                  ? "Terminer l'examen"
                  : "Question suivante"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
